import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { LANGS } from '@/i18n/translations';
import { GRACIES, proximaText } from '@/data/catalog';
import { X } from 'lucide-react';

// Popup de agradecimiento de cierre de edición.
//
// No se escribe aquí: el texto vive en el bloque "gracies" de
// festivalData.json, con sus tres idiomas. Si ese bloque no existe o tiene
// "actiu": false, este componente no pinta nada y la web queda exactamente
// como estaba.
//
// Se abre solo al cargar la portada, una vez por navegador: al cerrarlo se
// guarda el id del mensaje en localStorage. Cambiar el "id" en el JSON hace
// que vuelva a salir a todo el mundo (útil para el mensaje del año que viene).
//
// El mensaje se puede leer en los tres idiomas sin cambiar el de la web, con
// las mismas pestañas que la sinopsis de la ficha de espectáculo (ShowModal).

const STORAGE_KEY = 'cea-gracies-vist';
const OPEN_DELAY_MS = 900;

const OVERLAY_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL_MOTION = {
  initial: { opacity: 0, y: 24, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 24, scale: 0.98 },
  transition: { duration: 0.28, ease: 'easeOut' },
};

// El hashtag del texto se pinta como enlace a Instagram sin tocar el copy:
// se parte el párrafo por el propio hashtag.
const withHashtag = (text, tag, url) => {
  if (!tag || !url || !text.includes(tag)) return text;
  const [before, ...rest] = text.split(tag);
  return (
    <>
      {before}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-[var(--cea-accent)] hover:underline"
      >
        {tag}
      </a>
      {rest.join(tag)}
    </>
  );
};

export const GraciesModal = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  // Idioma en el que se lee el mensaje. Arranca en el de la web, pero se puede
  // cambiar solo aquí dentro sin tocar el resto del sitio.
  const [tab, setTab] = useState(lang);

  useEffect(() => { setTab(lang); }, [lang, open]);

  // Abrir sola al cargar, salvo que este mismo mensaje ya se haya cerrado.
  // Con ?gracies en la URL se fuerza a salir siempre: es la forma de
  // revisarlo sin tener que borrar datos del navegador.
  useEffect(() => {
    if (!GRACIES) return undefined;
    const forced = window.location.search.includes('gracies');
    let seen = null;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // localStorage puede no estar disponible (modo privado, cookies bloqueadas)
      console.warn('No s\'ha pogut llegir la preferència del missatge de gràcies:', e);
    }
    if (!forced && seen === GRACIES.id) return undefined;

    const timer = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, GRACIES.id);
    } catch (e) {
      console.warn('No s\'ha pogut desar la preferència del missatge de gràcies:', e);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, close]);

  if (!GRACIES) return null;

  const paragraphs = GRACIES.text[tab] || GRACIES.text.ca;
  // La fecha de la próxima edición se lee en el idioma de la pestaña, igual
  // que el resto del mensaje.
  const proxima = proximaText(tab);
  const titol = (GRACIES.titol || {})[tab] || '';
  const comiat = (GRACIES.comiat || {})[tab] || '';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[210] flex items-stretch sm:items-center justify-center p-0 sm:p-6"
          {...OVERLAY_FADE}
          data-testid="gracies-modal"
        >
          <div className="absolute inset-0 bg-[var(--cea-overlay)] backdrop-blur-sm" onClick={close} />

          <motion.div
            {...PANEL_MOTION}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gracies-title"
            className="cea-glow-soft relative z-10 flex w-full max-h-screen flex-col overflow-hidden border border-[var(--cea-border-strong)] bg-[#0b241b] sm:max-h-[88vh] sm:max-w-xl sm:rounded-2xl"
          >
            {/* halo de luz mint, el mismo gesto que el foco del flexo de la portada */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 h-56 w-72 -translate-x-1/2 rounded-full opacity-60"
              style={{ background: 'radial-gradient(ellipse at center, rgba(51,255,204,0.30) 0%, rgba(51,255,204,0) 70%)' }}
            />

            <button
              data-testid="gracies-modal-close"
              onClick={close}
              className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full border border-[var(--cea-border)] bg-[#040f0c]/80 text-white transition-colors duration-200 hover:text-[var(--cea-accent)]"
              aria-label={t.modal.close}
            >
              <X size={20} />
            </button>

            <div className="relative overflow-y-auto px-6 py-10 sm:px-10 sm:py-12">
              <div className="flex flex-col items-center text-center">
                <span className="brand-logo brand-logo--small" aria-hidden="true" />

                <span className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--cea-accent)]">
                  {t.hero.edition}
                </span>

                <h2
                  id="gracies-title"
                  className="cea-glow mt-2 font-display text-5xl font-normal uppercase leading-[0.85] tracking-[0.01em] text-white sm:text-6xl"
                >
                  {titol}
                </h2>

                <span className="mt-6 h-px w-16 bg-[var(--cea-border-strong)]" aria-hidden="true" />

                {/* Llegir el missatge en un altre idioma sense canviar el de la
                    web. Mateixes pestanyes que la sinopsi de l'espectacle. */}
                <div className="mt-6 inline-flex rounded-full border border-[var(--cea-border)] p-0.5">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      data-testid={`gracies-lang-${l.code}`}
                      onClick={() => setTab(l.code)}
                      aria-pressed={tab === l.code}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cea-accent)] ${
                        tab === l.code ? 'bg-[var(--cea-accent)] text-[#040f0c]' : 'text-white/55 hover:text-white'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 space-y-4 text-left">
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-white/80 sm:text-[15px]">
                    {withHashtag(p, GRACIES.hashtag, GRACIES.hashtagUrl)}
                  </p>
                ))}
              </div>

              {comiat && (
                <p className="cea-glow mt-9 text-center font-display text-3xl font-normal uppercase leading-none tracking-wide text-[var(--cea-accent)] sm:text-4xl">
                  {comiat}
                </p>
              )}

              {proxima && (
                <p
                  data-testid="gracies-proxima"
                  className="mt-4 text-center font-display text-xl font-normal uppercase leading-none tracking-wide text-white/80 sm:text-2xl"
                >
                  {proxima.dates}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
